<script>
import * as AdsV3Api from '@/api/advertisement-v4';
import RequireAdContract from '../common/RequireAdContract.vue';
import Content from '@/components/common/layout/Content.vue';
import { mapActions, mapGetters } from 'vuex';
import { APPS, PUBLISHED_APPS } from '@/store/types';
import ReportingChart from './ReportingChart.vue';
import CampaignsDonutCharts from './CampaignsDonutCharts.vue';
import CampaignsRatioCharts from './CampaignsRatioCharts.vue';
import CampaignsTable from '../common/CampaignsTable.vue';
import { truncate } from '@/utils/strings';
import ReportingPageIntro from './ReportingPageIntro.vue';
// import KeywordsReportTable from './KeywordsReportTable.vue';
import lang from '@/mixins/lang';
import ReportGenerateForm from './ReportGenerateForm.vue';

export default {
  name: 'ReportingPage',
  components: {
    RequireAdContract,
    Content,
    ReportingChart,
    CampaignsDonutCharts,
    CampaignsRatioCharts,
    CampaignsTable,
    ReportingPageIntro,
    ReportGenerateForm,
    // KeywordsReportTable,
  },
  mixins: [lang],
  data() {
    return {
      reportGenerated: false,
      series: [],
      dailyStats: null,
      dateLabels: [],
      selectedDateRange: null,
      campaigns: [],
      reportCampaigns: [],
      selectedCampaignIds: [],
      campaignsLoading: false,
      overallStatsLoading: false,
      dailyStatsLoading: false,
      ratioCharts: [],
      pieCharts: [],
      downloadingReport: false,
      initialReportParams: {
        campaigns: [],
        dateRange: [],
        packageName: '',
      },
    };
  },
  computed: {
    ...mapGetters({ publishedApps: PUBLISHED_APPS }),
    // selectedSearchCampaigns() {
    //   return this.reportCampaigns?.filter(
    //     campaign => campaign.campaignType === 'S',
    //   );
    // },
  },
  async created() {
    await this.fetchCampaigns();

    const { query } = this.$route;

    if (query.campaigns) {
      this.initialReportParams = {
        campaigns: query.campaigns,
        start: query.start,
        end: query.end,
      };

      this.generateReport(this.initialReportParams);
    }
  },
  methods: {
    ...mapActions({ fetchAppsList: APPS }),
    async fetchCampaigns() {
      try {
        this.campaignsLoading = true;
        this.campaigns = await AdsV3Api.getCampaigns();
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.campaignsLoading = false;
      }
    },
    generateReport(params) {
      if (params.export) {
        return this.downloadReport(params);
      }

      this.$router
        .push({
          path: this.$route.path,
          query: {
            campaigns: params.campaigns,
            start: params.start,
            end: params.end,
          },
        })
        .catch(() => {});

      this.reportGenerated = true;

      this.reportCampaigns = this.campaigns?.filter(({ uuid }) =>
        params.campaigns.includes(uuid),
      );

      this.fetchDailyStatsForReport(params);
      this.fetchOverallStatsForReport(params);
    },
    async fetchOverallStatsForReport(params) {
      try {
        this.overallStatsLoading = true;
        const reportParams = {
          campaignIds: [...params.campaigns],
          start: params.start,
          end: params.end,
        };
        const {
          ratioCharts,
          pieCharts,
          campaignsStats,
        } = await AdsV3Api.getReportOverallStats(reportParams);

        this.reportCampaigns = this.reportCampaigns?.map((campaign, i) => ({
          ...campaign,
          ...campaignsStats[campaign.uuid],
        }));

        this.ratioCharts = ratioCharts;
        this.pieCharts = pieCharts;
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.overallStatsLoading = false;
      }
    },
    async fetchDailyStatsForReport(params) {
      try {
        this.dailyStatsLoading = true;

        const reportParams = {
          campaignIds: [...params.campaigns],
          start: params.start,
          end: params.end,
        };

        const { dates, stats } = await AdsV3Api.getCampaignsDailyStats(
          reportParams,
        );

        this.dailyStats = stats;
        this.dateLabels = dates;
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.dailyStatsLoading = false;
      }
    },
    async downloadReport({ campaigns, dateRange }) {
      try {
        this.downloadingReport = true;
        const reportParams = {
          campaignIds: campaigns?.map(({ uuid }) => uuid),
          start: dateRange?.[0],
          end: dateRange?.[1],
        };
        await AdsV3Api.downloadCampaignsDailyReport(reportParams);
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.downloadingReport = false;
      }
    },
  },
};
</script>

<template>
  <RequireAdContract>
    <Content class="with-new-design reporting-page">
      <h4>{{ $t('ADVERTISEMENT.REPORTING.CAMPAIGNS_REPORTING') }}</h4>

      <ReportGenerateForm
        :campaigns="campaigns"
        :initial-value="initialReportParams"
        @submit="generateReport"
      />

      <template v-if="reportGenerated">
        <ReportingChart
          :stats="dailyStats"
          :campaigns="reportCampaigns"
          :x-axis="dateLabels"
          :loading="overallStatsLoading"
        />

        <CampaignsDonutCharts
          :charts="pieCharts"
          :campaigns="reportCampaigns"
          :loading="overallStatsLoading"
        />
        <CampaignsRatioCharts
          :charts="ratioCharts"
          :campaigns="reportCampaigns"
          :loading="overallStatsLoading"
        />
        <CampaignsTable
          v-loading="campaignsLoading"
          :stats-loading="overallStatsLoading"
          :campaigns="reportCampaigns"
          readonly
        />
      </template>
      <!-- <KeywordsReportTable
          v-if="false"
          :app="selectedApp"
          :search-campaigns="selectedSearchCampaigns"
          :date-range="selectedDateRange"
        /> -->
      <ReportingPageIntro v-else />
    </Content>
  </RequireAdContract>
</template>

<style lang="scss">
.reporting-page {
  &__app-select,
  &__app-select-popper {
    width: rem-calc(200);

    @include breakpoint(medium down) {
      width: 100%;
    }
  }

  &__date-picker {
    width: rem-calc(350);

    @include breakpoint(medium down) {
      width: 100%;
    }
  }
}
.reporting-filters__label {
  width: rem-calc(90px);
}
</style>
